import type { AdvisorSla } from '../types/portal'
import { tituloDelSla } from '../sla'

/**
 * Chip del reloj de respuesta: cuánto lleva el cliente esperando a una persona.
 *
 * Los umbrales los resuelve el backend (`shared/sla_asesor.py`) y son LOS
 * MISMOS con los que el bot decide reasignar. Por eso acá no se recalcula nada
 * a partir de fechas: si el navegador tuviera la hora corrida, la bandeja diría
 * "12 min · en plazo" sobre una conversación que el bot ya le sacó al asesor.
 *
 * Estados (minutos HÁBILES desde el 20-oct-2026):
 *   none     → nadie espera. No se pinta nada; es el caso normal.
 *   ok       → < 5 min, gris: informativo, no hace falta correr.
 *   warn     → ≥ 5 min, ámbar: aviso, el asesor debería contestar.
 *   breached → ≥ 10 min, rojo: pasa a otro asesor que no haya fallado hoy, o
 *              queda «para revisar»… salvo que ya la atienda una persona
 *              (`atendida`, DEF-001): ahí el plazo es de 30 min y queda con su
 *              asesor hasta que un supervisor la mueva. El texto lo arma
 *              `src/sla.ts`.
 *   `paused` → fuera del horario de atención el reloj no corre: gris, con
 *              «en pausa», sea cual sea el nivel.
 *
 * Existe porque hasta el 01-set-2026 el lado humano no tenía ningún timeout: si
 * el asesor no abría el portal, el cliente escribía al vacío y nadie se
 * enteraba — sin alerta, sin SLA y sin reasignación.
 */
export default function SlaChip({ sla, atendida = false, paraRevisar = false, compact = false }: {
  sla: AdvisorSla | undefined
  /** Una persona ya le escribió al cliente: el reloj no la reasigna. */
  atendida?: boolean
  /** Pasó por todos los asesores sin respuesta: no rota más. */
  paraRevisar?: boolean
  compact?: boolean
}) {
  if (!sla || sla.level === 'none') return null

  const estilo =
    sla.paused ? 'border-surface-border bg-surface text-ink-muted'
    : sla.level === 'breached' ? 'border-danger-border bg-danger-light text-danger'
    : sla.level === 'urgent' || sla.level === 'warn'
      ? 'border-warn-border bg-warn-light text-warn'
      : 'border-surface-border bg-surface text-ink-muted'

  const texto =
    sla.paused
      ? (compact ? `${sla.waiting_min} min · en pausa` : sla.label)
    : sla.level === 'breached'
      ? (compact ? `${sla.waiting_min} min sin responder` : `Sin responder hace ${sla.waiting_min} min`)
      : compact
        ? `${sla.waiting_min} min`
        : sla.label

  const titulo = tituloDelSla(sla, atendida, paraRevisar)

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-chip border px-2 py-0.5 text-pill font-bold ${estilo}`}
      title={titulo}
    >
      {sla.level !== 'ok' && (
        <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden>
          <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.3" />
          <path d="M6 3.4V6l1.8 1.2" stroke="currentColor" strokeWidth="1.3"
                strokeLinecap="round" />
        </svg>
      )}
      {texto}
    </span>
  )
}
